import React, { useState, useEffect } from 'react';

// Utility to load image - return URL for all, console errors are suppressed globally
const loadImageSilently = async (url: string): Promise<string> => {
  return url; // Return the URL, console errors are suppressed in main.tsx
};

// Component to handle silent image loading
const SilentImage: React.FC<{
  src: string;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
  fetchpriority?: 'high' | 'low' | 'auto';
  onLoad?: () => void;
  onError?: () => void;
}> = ({ src, alt, className, loading, decoding, fetchpriority, onLoad, onError }) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    setHasError(false);

    const loadImage = async () => {
      const loadedSrc = await loadImageSilently(src);
      if (isMounted) {
        setImageSrc(loadedSrc);
      }
    };

    loadImage();

    return () => {
      isMounted = false;
    };
  }, [src]);

  // Custom onError to suppress console errors
  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  const handleLoad = () => {
    onLoad?.();
  };

  if (hasError) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100`}>
        <span className="text-6xl">🥩</span>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      loading={loading}
      decoding={decoding}
      fetchpriority={fetchpriority}
      onLoad={handleLoad}
      onError={handleError}
    />
  );
};

export default SilentImage;